import axios from 'axios';

// 导入本地数据文件
import homeData from '../assets/home.json';
import travelRadarData from '../assets/travel_radar.json';

// 基础URL (用于远程加载)
const BASE_URL = 'https://cdn-info.broadsolutionsgroup.com/articles/website-15/';

// 切换数据加载模式: 'local' 或 'remote'
const DATA_MODE = 'remote';

// 用于缓存获取的数据，避免重复请求
const dataCache = {
  home: null,
  travel_radar: null
};

// 用于缓存正在进行的请求Promise
const ongoingRequests = {};

// 本地数据映射
const localDataMap = {
  home: homeData,
  travel_radar: travelRadarData
};

// 处理远程数据格式，确保本地和远程数据结构一致
const processRemoteData = (data, fileName) => {
  if (!data) return null;
  
  // 处理travel_radar.json格式
  if (fileName === 'travel_radar') {
    // travel_radar.json包含categories数组，每个category包含articles数组
    if (data.categories && Array.isArray(data.categories)) {
      const processedCategories = data.categories.map(category => ({
        ...category,
        articles: processArticles(category.articles || [], category.name)
      }));
      return { ...data, categories: processedCategories };
    }
    return data;
  }
  
  // 处理home.json格式
  if (fileName === 'home') {
    if (data.allCategories && Array.isArray(data.allCategories)) {
      const processedCategories = data.allCategories.map(category => ({
        ...category,
        list: processArticles(category.list || [], category.name)
      }));
      return { ...data, allCategories: processedCategories };
    }
    return data;
  }
  
  return data;
};

// 处理文章数据，统一字段格式
const processArticles = (articles, categoryName) => {
  if (!articles || !Array.isArray(articles)) return [];
  
  return articles.map(article => {
    // 处理文章slug (如果不存在)
    const slug = article.slug || article.id;
    
    // 处理图片URL
    const imageUrl = article.imageUrl || article.image;
    
    // 处理阅读时间
    const readTime = article.readTime || article.duration || "3 min read";
    
    // 处理文章摘要
    const excerpt = article.excerpt || article.description;
    
    // 返回处理后的文章对象，确保category字段正确设置
    return {
      ...article,
      slug,
      imageUrl,
      readTime,
      excerpt,
      category: categoryName || article.category
    };
  });
};

// 获取JSON数据 (根据模式选择本地或远程)
const fetchJsonData = async (fileName) => {
  // If a request for this fileName is already in progress, return its promise
  if (ongoingRequests[fileName]) {
    return ongoingRequests[fileName];
  }

  // 如果已经缓存，直接返回 resolved data
  if (dataCache[fileName]) {
    return dataCache[fileName]; // This will be the resolved data
  }
  
  // Create a new promise for the fetch operation
  const fetchPromise = (async () => {
    try {
      let data;
      
      if (DATA_MODE === 'local') {
        data = localDataMap[fileName] || null;
        if (!data) {
          console.error(`本地数据文件不存在: ${fileName}.json`);
          return null; 
        }
        // 处理本地数据格式
        data = processRemoteData(data, fileName);
      } else {
        const response = await axios.get(`${BASE_URL}${fileName}.json`);
        data = response.data;
        data = processRemoteData(data, fileName);
      }
      
      dataCache[fileName] = data; // Cache the resolved data
      return data;
    } catch (error) {
      console.error(`Error fetching ${fileName}.json:`, error);
      if (DATA_MODE === 'remote' && localDataMap[fileName]) {
        console.log(`尝试使用本地数据作为回退: ${fileName}.json`);
        const fallbackData = processRemoteData(localDataMap[fileName], fileName);
        dataCache[fileName] = fallbackData; // Cache fallback data
        return fallbackData;
      }
      return null; // Resolve with null on error if no fallback
    } finally {
      // Once the promise settles (resolves or rejects), remove it from ongoingRequests
      delete ongoingRequests[fileName];
    }
  })();

  // Store the promise in ongoingRequests
  ongoingRequests[fileName] = fetchPromise;
  return fetchPromise;
};

// 获取所有类别 (从travel_radar.json中获取)
export const getCategories = async () => {
  try {
    const travelData = await fetchJsonData('travel_radar');
    if (!travelData || !travelData.categories) return [];
    
    return travelData.categories.map(category => ({
      name: category.name,
      slug: category.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and'),
      id: category.id
    }));
  } catch (error) {
    console.error('获取类别失败:', error);
    return [];
  }
};

// 获取特定类别的所有文章
export const getArticlesByCategory = async (categorySlug) => {
  try {
    const travelData = await fetchJsonData('travel_radar');
    if (!travelData || !travelData.categories) return [];
    
    // 根据slug查找对应的类别
    const category = travelData.categories.find(cat => {
      const catSlug = cat.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
      return catSlug === categorySlug.toLowerCase();
    });
    
    if (!category) {
      // 如果在travel_radar中找不到，尝试从home中查找
      return getCategoryArticlesFromHome(categorySlug);
    }
    
    return category.articles || [];
  } catch (error) {
    console.error(`获取类别文章失败: ${categorySlug}`, error);
    return [];
  }
};

// 从home.json中获取特定类别的文章
const getCategoryArticlesFromHome = async (categorySlug) => {
  try {
    const allHomeArticles = await getAllArticles();
    return allHomeArticles.filter(article => {
      if (!article.category) return false;
      const articleCategorySlug = article.category.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
      return articleCategorySlug === categorySlug.toLowerCase();
    });
  } catch (error) {
    console.error(`从home获取类别文章失败: ${categorySlug}`, error);
    return [];
  }
};

// 获取所有文章 
export const getAllArticles = async () => {
  try {
    let allArticles = [];
    
    // 从travel_radar.json获取文章
    const travelData = await fetchJsonData('travel_radar');
    if (travelData && travelData.categories) {
      travelData.categories.forEach(category => {
        if (category.articles && Array.isArray(category.articles)) {
          // 为每篇文章添加category字段
          const articlesWithCategory = category.articles.map(article => ({
            ...article,
            category: category.name
          }));
          allArticles = [...allArticles, ...articlesWithCategory];
        }
      });
    }
    
    // 从home.json获取文章作为补充，同样正确设置category字段
    const homeData = await fetchJsonData('home');
    if (homeData && homeData.allCategories) {
      homeData.allCategories.forEach(category => {
        if (category.list && Array.isArray(category.list)) {
          // 确保每篇文章的category字段正确设置
          const homeArticlesWithCategory = category.list.map(article => ({
            ...article,
            // 如果文章已有category字段就保持，否则用分类名称
            category: article.category || category.name
          }));
          allArticles = [...allArticles, ...homeArticlesWithCategory];
        }
      });
    }
    
    return allArticles;
  } catch (error) {
    console.error("获取所有文章失败", error);
    return [];
  }
};

// 获取首页 For You 内容
export const getForYouArticles = async () => {
  try {
    const homeData = await fetchJsonData('home');
    if (!homeData || !homeData.allCategories) return [];
    
    const forYouCategory = homeData.allCategories.find(cat => cat.name === "For you");
    let forYouArticles = forYouCategory ? forYouCategory.list || [] : [];
    
    // 确保For You文章有正确的category字段
    const allArticles = await getAllArticles();
    forYouArticles = forYouArticles.map(forYouArticle => {
      // 查找匹配的文章以获取正确的category
      const matchedArticle = allArticles.find(article => 
        article.id === forYouArticle.id || 
        article.title === forYouArticle.title || 
        article.slug === forYouArticle.slug
      );
      
      return {
        ...forYouArticle,
        category: matchedArticle ? matchedArticle.category : (forYouArticle.category || 'Hidden Destinations')
      };
    });
    
    return forYouArticles;
  } catch (error) {
    console.error("获取For You文章失败", error);
    return [];
  }
};

// 获取首页 Trending 内容
export const getTrendingArticles = async (limit = 3) => {
  try {
    const homeData = await fetchJsonData('home');
    if (!homeData || !homeData.allCategories) return [];
    
    const trendingCategory = homeData.allCategories.find(cat => cat.name === "Trending");
    let trendingArticles = trendingCategory ? (trendingCategory.list || []).slice(0, limit) : [];
    
    // 确保Trending文章有正确的category字段
    const allArticles = await getAllArticles();
    trendingArticles = trendingArticles.map(trendingArticle => {
      // 查找匹配的文章以获取正确的category
      const matchedArticle = allArticles.find(article => 
        article.id === trendingArticle.id || 
        article.title === trendingArticle.title || 
        article.slug === trendingArticle.slug
      );
      
      return {
        ...trendingArticle,
        category: matchedArticle ? matchedArticle.category : (trendingArticle.category || 'Hidden Destinations')
      };
    });
    
    return trendingArticles;
  } catch (error) {
    console.error("获取Trending文章失败", error);
    return [];
  }
};

// 获取所有 Trending 文章
export const getAllTrendingArticles = async () => {
  try {
    const homeData = await fetchJsonData('home');
    if (!homeData || !homeData.allCategories) return [];
    
    const trendingCategory = homeData.allCategories.find(cat => cat.name === "Trending");
    let trendingArticles = trendingCategory ? trendingCategory.list || [] : [];
    
    // 确保Trending文章有正确的category字段
    const allArticles = await getAllArticles();
    trendingArticles = trendingArticles.map(trendingArticle => {
      // 查找匹配的文章以获取正确的category
      const matchedArticle = allArticles.find(article => 
        article.id === trendingArticle.id || 
        article.title === trendingArticle.title || 
        article.slug === trendingArticle.slug
      );
      
      return {
        ...trendingArticle,
        category: matchedArticle ? matchedArticle.category : (trendingArticle.category || 'Hidden Destinations')
      };
    });
    
    return trendingArticles;
  } catch (error) {
    console.error("获取所有Trending文章失败", error);
    return [];
  }
};

// 获取精选文章
export const getFeaturedArticles = async () => {
  try {
    const homeData = await fetchJsonData('home');
    if (!homeData || !homeData.allCategories) return [];
    
    // 从首页分类中获取一些文章作为特色
    const articles = homeData.allCategories.flatMap(category => category.list || []).slice(0, 6);
    return articles;
  } catch (error) {
    console.error("获取精选文章失败", error);
    return [];
  }
};

// 获取最新文章
export const getLatestArticles = async (limit = 6) => {
  try {
    const allArticles = await getAllArticles();
    
    // 按创建时间排序
    const sortedArticles = [...allArticles].sort((a, b) => {
      const dateA = a.createTime ? new Date(a.createTime) : new Date();
      const dateB = b.createTime ? new Date(b.createTime) : new Date();
      return dateB - dateA; // 降序排列，最新的在前
    });
    
    return sortedArticles.slice(0, limit);
  } catch (error) {
    console.error("获取最新文章失败", error);
    return [];
  }
};

// Helper function to slugify category names for comparison
const slugifyCategory = (categoryName) => {
  if (!categoryName) return '';
  return String(categoryName).toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
};

// 获取相关文章
export const getRelatedArticles = async (currentArticleCategory, currentArticleId, limit = 4) => {
  try {
    const allArticles = await getAllArticles();
    
    if (!allArticles || allArticles.length === 0) {
      return [];
    }

    const targetCategorySlug = slugifyCategory(currentArticleCategory);

    // 筛选出同一类别且非当前文章的文章
    const related = allArticles.filter(article => {
      if (!article.category || !article.id) return false;
      const articleCategorySlug = slugifyCategory(article.category);
      return articleCategorySlug === targetCategorySlug && String(article.id) !== String(currentArticleId);
    });
    
    return related.slice(0, limit);
    
  } catch (error) {
    console.error('Error fetching related articles:', error);
    return [];
  }
};

// 搜索文章 (全文搜索) - 只搜索travel_radar.json避免重复
export const searchArticles = async (query) => {
  try {
    if (!query || query.trim() === '') return [];
    
    const travelData = await fetchJsonData('travel_radar');
    if (!travelData || !travelData.categories) return [];
    
    const normalizedQuery = query.toLowerCase();
    let allArticles = [];
    
    // 只从travel_radar.json获取所有文章
    travelData.categories.forEach(category => {
      if (category.articles && Array.isArray(category.articles)) {
        const articlesWithCategory = category.articles.map(article => ({
          ...article,
          category: category.name
        }));
        allArticles = [...allArticles, ...articlesWithCategory];
      }
    });
    
    // 根据标题、描述、内容搜索
    return allArticles.filter(article => {
      return (
        (article.title && article.title.toLowerCase().includes(normalizedQuery)) ||
        (article.description && article.description.toLowerCase().includes(normalizedQuery)) ||
        (article.content && article.content.toLowerCase().includes(normalizedQuery))
      );
    });
  } catch (error) {
    console.error("搜索文章失败", error);
    return [];
  }
};

// 通过ID获取文章
export const getArticleById = async (id) => {
  try {
    const allArticles = await getAllArticles();
    return allArticles.find(article => article.id === parseInt(id) || article.id === id) || null;
  } catch (error) {
    console.error(`通过ID获取文章失败: ${id}`, error);
    return null;
  }
}; 